import { h } from 'preact'
import { useLayoutEffect, useEffect, useState, useCallback, useMemo } from 'preact/hooks'
import dayjs from 'dayjs'
import { BookingStep, PatientType, Sex } from '../../models/enums'
import { Patient, AdditionalInfo } from '../../models/saveAppointment'
import { useStateValue } from '../../state-management/store'
import { bookAppointment } from '../../state-management/actions'
import { DATE_FORMAT } from '../../shared/const'
import isoDate from '../../shared/isoDate'
import { softGreen } from '../../shared/colors'
import globalVars from '../../shared/global'
import locationHash from '../../shared/locationHash'
import { StepBody } from '../step-body'
import { ErrorMessage } from '../error-message'
import { SelfSchedulerFooter } from '../self-scheduler-footer'
import { Spinner } from '../spinner'
import * as style from './style.css'
import { PatientDetails } from '../../models/patient'
import { useSocketService } from '../socket-provider'
import { INVALID_PHONE_NUMBER_ENTERED_ERROR_MESSAGE } from '../../shared/customErrorMessages'
import { uuidv4 } from '../../shared/uuid'

const Reserving: preact.FunctionalComponent = () => {
    const [
        {
            customization,
            errorMessage,
            statusCode,
            patientType,
            patientId,
            patientDetails,
            additionalDetails,
            selectedLocationId,
            selectedAppointmentTime,
            hasAdditionalInformation,
            startingURL,
        },
        dispatch,
    ] = useStateValue()
    const socket = useSocketService()

    const [appointmentId, setAppointmentId] = useState<string>('')
    const [hasBookingErrors, setHasBookingErrors] = useState<boolean>(false)

    const patient: Patient = useMemo(() => {
        const buildNewPatientObject = (patientDetails: PatientDetails): Patient => {
            const newPatient: Patient = {
                first_name: patientDetails.firstName.value,
                last_name: patientDetails.lastName.value,
                birth_date: isoDate(dayjs(patientDetails.dateOfBirth.value, DATE_FORMAT).toDate()),
                mobile_phone: patientDetails.phone.value,
                email_address: patientDetails.emailAddress.value,
                has_insurance: patientDetails.hasInsurance.value === 'yes',
            }

            if (patientDetails.sex) {
                newPatient.gender = patientDetails.sex.value as Sex
            }

            return newPatient
        }
        const patient =
            patientType === PatientType.EXISTING
                ? ({
                      id: patientId,
                      mobile_phone: patientDetails.phone.value,
                      email_address: patientDetails.emailAddress.value,
                  } as Patient)
                : buildNewPatientObject(patientDetails)

        if (hasAdditionalInformation) {
            const additional_info: AdditionalInfo = {}
            if ((additional_info.notes = additionalDetails.additionalNotes?.value)) {
                additional_info.notes = additionalDetails.additionalNotes.value
            }
            if (patientDetails.hasInsurance.value === 'yes') {
                if (additionalDetails.insuranceProvider?.value) {
                    additional_info.insurance = additionalDetails.insuranceProvider.value
                }
                if (additionalDetails.insuranceGroup?.value) {
                    additional_info.insurance_group = additionalDetails.insuranceGroup.value
                }
                if (additionalDetails.isPolicyHolder?.value) {
                    additional_info.patient_is_policy_holder = additionalDetails.isPolicyHolder.value === 'yes'
                }
                if (additionalDetails.insurancePhone?.value) {
                    additional_info.insurance_phone = additionalDetails.insurancePhone.value
                }
                if (additionalDetails.policyHolderIDSSN?.value) {
                    additional_info.insurance_member_id_ssn = additionalDetails.policyHolderIDSSN.value
                }
                if (additionalDetails.isPolicyHolder?.value === 'no') {
                    if (additionalDetails.policyHolderFirstName?.value) {
                        additional_info.policy_holder_first_name = additionalDetails.policyHolderFirstName.value
                    }
                    if (additionalDetails.policyHolderLastName?.value) {
                        additional_info.policy_holder_last_name = additionalDetails.policyHolderLastName.value
                    }
                    if (additionalDetails.policyHolderDOB?.value) {
                        additional_info.policy_holder_date_of_birth = isoDate(
                            dayjs(additionalDetails.policyHolderDOB.value, DATE_FORMAT).toDate()
                        )
                    }
                }
            }
            patient.additional_info = additional_info
        }

        return patient
    }, [
        patientDetails.dateOfBirth.value,
        patientDetails.emailAddress.value,
        patientDetails.firstName.value,
        patientDetails.lastName.value,
        patientDetails.phone.value,
        patientDetails.sex.value,
        patientId,
        patientType,
        hasAdditionalInformation,
        additionalDetails,
        customization?.scheduling_additional_features_and_fields,
    ])

    const handleBookAppointment = useCallback(async () => {
        try {
            const transactionId = uuidv4()

            socket?.subscribe(transactionId)

            const startingUrlDecoded =
                (locationHash.startingUrl && decodeURIComponent(locationHash.startingUrl)) || startingURL

            const bookingId = await bookAppointment(dispatch, {
                locationId: selectedLocationId,
                procedureId: patientDetails.reasonOfAppointment.value as string,
                payload: {
                    datetime: selectedAppointmentTime,
                    patient,
                    chairfillId: globalVars.isRSVP || locationHash.chairfillId,
                    transactionId,
                    ...(patientDetails.provider.value
                        ? {
                              providerId: patientDetails.provider.value,
                          }
                        : {}),
                    ...(startingUrlDecoded && startingUrlDecoded !== 'undefined'
                        ? { starting_url: startingUrlDecoded }
                        : {}),
                },
            })

            if (bookingId) {
                setAppointmentId(bookingId)
            } else {
                setHasBookingErrors(true)
            }
        } catch (e) {
            setHasBookingErrors(true)
        }
    }, [dispatch, patient, patientDetails.reasonOfAppointment.value, selectedAppointmentTime, selectedLocationId])

    useLayoutEffect(() => {
        handleBookAppointment()
    }, [handleBookAppointment])

    useEffect(() => {
        let interval: NodeJS.Timeout | null = null

        if (appointmentId) {
            interval = setInterval(() => {
                socket.disconnect()
                dispatch({
                    type: 'CHANGE_STEP',
                    bookingStep: BookingStep.TO_BE_BOOKED,
                })
            }, 31000)
        }

        return () => {
            if (interval) {
                clearInterval(interval)
            }
        }
    }, [appointmentId])

    useEffect(() => {
        if (statusCode === 422) {
            dispatch({
                type: 'CHANGE_STEP',
                bookingStep: BookingStep.BOOKING_FAILED,
            })
        }
    }, [statusCode])

    const handleTryAgain = () => {
        setHasBookingErrors(false)
        handleBookAppointment()
    }

    return (
        <section class={style.reservingContainer}>
            <StepBody>
                {Boolean(errorMessage) && <ErrorMessage>{errorMessage}</ErrorMessage>}
                {!hasBookingErrors && (
                    <div class={style.reserving}>{!hasBookingErrors && <Spinner width={7} color={softGreen} />}</div>
                )}
            </StepBody>
            {!hasBookingErrors ? (
                <SelfSchedulerFooter />
            ) : (
                <SelfSchedulerFooter
                    backStep={BookingStep.CONFIRM}
                    onNext={handleTryAgain}
                    isNextDisabled={Boolean(errorMessage === INVALID_PHONE_NUMBER_ENTERED_ERROR_MESSAGE)}
                    nextLabel="Try again"
                />
            )}
        </section>
    )
}

export default Reserving
